import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Alert,
  Grid,
  Tabs,
  Tab,
  Paper,
  CircularProgress,
} from '@mui/material';
import {
  Download as DownloadIcon,
  Settings as SettingsIcon,
  AccountBalance as BillingIcon,
  Support as SupportIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { useLocation } from 'react-router-dom';
import DesktopAppDownload from './DesktopAppDownload';
import DesktopAppSetupInstructions from './DesktopAppSetupInstructions';
import SupportTicketForm from '../support/SupportTicketForm';
import SubscriptionManagement from '../subscription/SubscriptionManagement';
import { useDesktopTier } from '../../hooks/useDesktopTier';
import { useAuth } from '../../contexts/AuthContext';
import { Subscription, BillingHistory, User } from '../../types/dashboard';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`limited-mode-tabpanel-${index}`}
      aria-labelledby={`limited-mode-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

interface LimitedModeViewProps {
  user?: User | null;
  tenant?: { id: string } | null;
}

const LimitedModeView: React.FC<LimitedModeViewProps> = ({ user, tenant }) => {
  const location = useLocation();
  const { token } = useAuth();
  const { isDesktopTier, loading: tierLoading } = useDesktopTier();
  const [activeTab, setActiveTab] = useState(0);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [billingHistory, setBillingHistory] = useState<BillingHistory[]>([]);
  const [loading, setLoading] = useState(true);

  // Check if redirected from restricted route
  const isRedirected = location.state?.restricted === true;
  const fromPath = location.state?.from;

  useEffect(() => {
    loadSubscriptionData();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load when token/user/tenant change
  }, [token, user, tenant]);

  const loadSubscriptionData = async () => {
    try {
      setLoading(true);
      // Load subscription and billing data
      // This would typically come from API calls
      // For now, using mock data structure
      // Use setTimeout to ensure React state updates are batched properly
      await new Promise(resolve => setTimeout(resolve, 0));
      
      if (user && tenant) {
        // TODO: Load actual subscription and billing data from API
        setSubscription({
          plan: 'desktop',
          status: 'active',
          webhookLimit: 4,
          webhookUsage: 0,
          storageLimit: 0.1,
          storageUsage: 0,
          expiresAt: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
          autoRenew: true,
        });
        setBillingHistory([]);
      }
    } catch (error) {
      console.error('Error loading subscription data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  if (tierLoading || loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  // If not desktop tier, show message (shouldn't happen if route protection works)
  if (!isDesktopTier) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="info">
          This view is only available for desktop tier subscribers.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header Banner */}
      <Paper
        elevation={2}
        sx={{
          p: 3,
          mb: 4,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
        }}
      >
        <Box display="flex" alignItems="center" gap={2} mb={2}>
          <InfoIcon sx={{ fontSize: 40 }} />
          <Box>
            <Typography variant="h4" component="h1" gutterBottom>
              Desktop App Required
            </Typography>
            <Typography variant="body1">
              Your subscription includes access to the desktop application for toast notification capture.
              Please download and install the desktop app to configure alerts and manage your trading automation.
            </Typography>
          </Box>
        </Box>

        {isRedirected && fromPath && (
          <Alert severity="warning" sx={{ mt: 2, bgcolor: 'rgba(255, 255, 255, 0.2)' }}>
            <Typography variant="body2">
              <strong>Note:</strong> The {fromPath} feature is only available in the desktop app.
              Please download and install the desktop app to access this functionality.
            </Typography>
          </Alert>
        )}
      </Paper>

      {/* Tabs Navigation */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          aria-label="limited mode tabs"
        >
          <Tab icon={<DownloadIcon />} iconPosition="start" label="Download" />
          <Tab icon={<SettingsIcon />} iconPosition="start" label="Setup" />
          <Tab icon={<SettingsIcon />} iconPosition="start" label="Subscription" />
          <Tab icon={<BillingIcon />} iconPosition="start" label="Billing" />
          <Tab icon={<SupportIcon />} iconPosition="start" label="Support" />
        </Tabs>
      </Paper>

      {/* Tab Panels */}
      <TabPanel value={activeTab} index={0}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <DesktopAppDownload />
          </Grid>
          <Grid item xs={12} md={6}>
            <DesktopAppSetupInstructions />
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={activeTab} index={1}>
        <DesktopAppSetupInstructions />
      </TabPanel>

      <TabPanel value={activeTab} index={2}>
        {subscription && user && (
          <SubscriptionManagement
            subscription={subscription}
            billingHistory={billingHistory}
            user={user}
            onPlanChange={(newPlan) => {
              console.log('Plan change requested:', newPlan);
              // TODO: Implement plan change
            }}
            onPaymentMethodUpdate={() => {
              console.log('Payment method update requested');
              // TODO: Implement payment method update
            }}
            onUserInfoUpdate={(userInfo) => {
              console.log('User info update requested:', userInfo);
              // TODO: Implement user info update
            }}
          />
        )}
      </TabPanel>

      <TabPanel value={activeTab} index={3}>
        {subscription && user && (
          <SubscriptionManagement
            subscription={subscription}
            billingHistory={billingHistory}
            user={user}
            onPlanChange={() => {}}
            onPaymentMethodUpdate={() => {}}
            onUserInfoUpdate={() => {}}
          />
        )}
      </TabPanel>

      <TabPanel value={activeTab} index={4}>
        <SupportTicketForm />
      </TabPanel>
    </Container>
  );
};

export default LimitedModeView;

